import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const CourseImport = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [departments, setDepartments] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [semester, setSemester] = useState('');
  const [selectedTrack, setSelectedTrack] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [courses, setCourses] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchDepartments();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    if (selectedDepartment || selectedAcademicYear) {
      setSelectedTrack('');
      fetchTracks(selectedDepartment, selectedAcademicYear);
    } else {
      setTracks([]);
    }
  }, [selectedDepartment, selectedAcademicYear]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Erreur chargement départements:', error);
    }
  };

  const fetchTracks = async (departmentId, academicYearId) => {
    try {
      const params = new URLSearchParams();
      if (departmentId) params.append('department', departmentId);
      if (academicYearId) params.append('academicYear', academicYearId);
      const response = await axios.get(`${API_URL}/api/tracks?${params.toString()}`);
      setTracks(response.data);
    } catch (error) {
      console.error('Erreur chargement parcours:', error);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/academic-years`);
      setAcademicYears(response.data);
    } catch (error) {
      console.error('Erreur chargement années universitaires:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setResults(null);
    }
  };

  const handlePreview = async () => {
    if (!file || !semester || !selectedAcademicYear || !selectedTrack) {
      showError('Veuillez remplir tous les champs avant la prévisualisation');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('semester', semester);
    formData.append('trackId', selectedTrack);
    formData.append('academicYearId', selectedAcademicYear);

    try {
      setImporting(true);
      const response = await axios.post(`${API_URL}/api/import/courses/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreview(response.data);
      setCourses(response.data.courses);
      showSuccess(`${response.data.totalCourses} cours détectés`);
    } catch (error) {
      console.error('Erreur prévisualisation:', error);
      showError(error.response?.data?.message || 'Erreur lors de la prévisualisation');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (courses.length > 0) {
      // Import depuis prévisualisation
      if (!selectedDepartment || !semester || !selectedAcademicYear || !selectedTrack) {
        showError('Veuillez remplir tous les champs obligatoires');
        return;
      }

      try {
        setImporting(true);
        const response = await axios.post(`${API_URL}/api/import/courses/from-preview`, {
          courses: courses,
          departmentId: selectedDepartment,
          semester: semester,
          trackId: selectedTrack,
          academicYearId: selectedAcademicYear
        });
        
        setResults(response.data);
        showSuccess(`Import terminé: ${response.data.results.success} cours créés`);
        
        // Reset
        setFile(null);
        setPreview(null);
        setCourses([]);
        document.getElementById('fileInput').value = '';
      } catch (error) {
        console.error('Erreur import:', error);
        showError(error.response?.data?.message || 'Erreur lors de l\'import');
      } finally {
        setImporting(false);
      }
    } else {
      // Import direct depuis fichier
      if (!file || !selectedDepartment || !semester || !selectedAcademicYear || !selectedTrack) {
        showError('Veuillez remplir tous les champs obligatoires');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('departmentId', selectedDepartment);
      formData.append('semester', semester);
      formData.append('trackId', selectedTrack);
      formData.append('academicYearId', selectedAcademicYear);

      try {
        setImporting(true);
        const response = await axios.post(`${API_URL}/api/import/courses`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setResults(response.data);
        showSuccess(`Import terminé: ${response.data.results.success} cours créés`);
        
        // Reset
        setFile(null);
        setPreview(null);
        document.getElementById('fileInput').value = '';
      } catch (error) {
        console.error('Erreur import:', error);
        showError(error.response?.data?.message || 'Erreur lors de l\'import');
      } finally {
        setImporting(false);
      }
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse({
      ...course,
      trackId: course.trackId || selectedTrack,
      academicYearId: course.academicYearId || selectedAcademicYear
    });
    setShowEditModal(true);
  };

  const handleSaveCourse = () => {
    setCourses(courses.map(course => 
      course.tempId === editingCourse.tempId ? editingCourse : course
    ));
    setShowEditModal(false);
    setEditingCourse(null);
  };

  const handleToggleCourse = (tempId) => {
    setCourses(courses.map(course => {
      if (course.tempId === tempId) {
        return {
          ...course,
          status: course.status === 'excluded' ? 'new' : 'excluded'
        };
      }
      return course;
    }));
  };

  const renderPreview = () => {
    if (!courses || courses.length === 0) return null;

    const activeCount = courses.filter(c => c.status !== 'excluded').length;
    const excludedCount = courses.filter(c => c.status === 'excluded').length;

    return (
      <div className="mt-6 bg-white rounded-lg border p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">📋 Prévisualisation & Édition</h3>
          <div className="text-sm text-gray-600">
            {activeCount} à importer • {excludedCount} exclus
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded">
            <div className="text-sm text-blue-600">Total</div>
            <div className="text-2xl font-bold text-blue-800">{courses.length}</div>
          </div>
          <div className="bg-green-50 p-3 rounded">
            <div className="text-sm text-green-600">À importer</div>
            <div className="text-2xl font-bold text-green-800">{activeCount}</div>
          </div>
          <div className="bg-red-50 p-3 rounded">
            <div className="text-sm text-red-600">Exclus</div>
            <div className="text-2xl font-bold text-red-800">{excludedCount}</div>
          </div>
          <div className="bg-purple-50 p-3 rounded">
            <div className="text-sm text-purple-600">Parcours</div>
            <div className="text-2xl font-bold text-purple-800">
              {[...new Set(courses.map(c => c.track))].length}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2">Inclure</th>
                <th className="text-left p-2">Code</th>
                <th className="text-left p-2">Nom</th>
                <th className="text-left p-2">Année</th>
                <th className="text-left p-2">Parcours</th>
                <th className="text-left p-2">Sem</th>
                <th className="text-left p-2">Crédits</th>
                <th className="text-left p-2">Heures</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {courses.map((course) => (
                <tr key={course.tempId} className={course.status === 'excluded' ? 'bg-red-50 opacity-60' : ''}>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={course.status !== 'excluded'}
                      onChange={() => handleToggleCourse(course.tempId)}
                      className="rounded"
                    />
                  </td>
                  <td className="p-2 font-medium">{course.code}</td>
                  <td className="p-2">{course.name}</td>
                  <td className="p-2">{course.academicYearName || 'N/A'}</td>
                  <td className="p-2">{course.trackName || 'N/A'}</td>
                  <td className="p-2">{course.semester}</td>
                  <td className="p-2">{course.credits}</td>
                  <td className="p-2">{course.volumeGlobal}h</td>
                  <td className="p-2">
                    <button
                      onClick={() => handleEditCourse(course)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      ✏️ Éditer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    return (
      <div className="mt-6 bg-white rounded-lg border p-4">
        <h3 className="text-lg font-medium mb-4">✅ Résultats d'import</h3>
        
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 p-3 rounded">
            <div className="text-sm text-green-600">Succès</div>
            <div className="text-2xl font-bold text-green-800">{results.results.success}</div>
          </div>
          <div className="bg-yellow-50 p-3 rounded">
            <div className="text-sm text-yellow-600">Doublons</div>
            <div className="text-2xl font-bold text-yellow-800">{results.results.duplicates}</div>
          </div>
          <div className="bg-red-50 p-3 rounded">
            <div className="text-sm text-red-600">Erreurs</div>
            <div className="text-2xl font-bold text-red-800">{results.results.errors}</div>
          </div>
          <div className="bg-blue-50 p-3 rounded">
            <div className="text-sm text-blue-600">Total</div>
            <div className="text-2xl font-bold text-blue-800">{results.totalCourses}</div>
          </div>
        </div>

        {results.results.details.length > 0 && (
          <div className="max-h-64 overflow-y-auto">
            <h4 className="font-medium mb-2">Détails :</h4>
            <div className="space-y-1">
              {results.results.details.map((detail, index) => (
                <div key={index} className={`text-sm p-2 rounded ${
                  detail.status === 'success' ? 'bg-green-50 text-green-800' :
                  detail.status === 'duplicate' ? 'bg-yellow-50 text-yellow-800' :
                  'bg-red-50 text-red-800'
                }`}>
                  <span className="font-medium">{detail.code}</span>
                  {detail.name && <span> - {detail.name}</span>}
                  <span className="ml-2 text-xs">({detail.message})</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">📊 Import de Cours depuis Excel</h1>
          <p className="text-gray-600 mt-1">
            Importez vos cours depuis un fichier Excel avec le format UE/ECUE
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="space-y-4">
            {/* Informations de base */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Département *
                </label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Sélectionner un département</option>
                  {departments.map(dept => (
                    <option key={dept._id} value={dept._id}>{dept.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Année universitaire *
                </label>
                <select
                  value={selectedAcademicYear}
                  onChange={(e) => setSelectedAcademicYear(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Sélectionner une année</option>
                  {academicYears.map(year => (
                    <option key={year._id} value={year._id}>{year.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Semestre *
                </label>
                <select
                  value={semester}
                  onChange={(e) => setSemester(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Sélectionner un semestre</option>
                  <option value="1">Semestre 1</option>
                  <option value="2">Semestre 2</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parcours *
                </label>
                <select
                  value={selectedTrack}
                  onChange={(e) => setSelectedTrack(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={!selectedDepartment && !selectedAcademicYear}
                >
                  <option value="">Sélectionner un parcours</option>
                  {tracks.map(track => (
                    <option key={track._id} value={track._id}>{track.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sélection fichier */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fichier Excel (.xlsx)
              </label>
              <input
                id="fileInput"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format attendu : Colonnes Cours, Global, Cours, TD, TP avec données sur les lignes suivantes
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handlePreview}
                disabled={!file || importing}
                className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50"
              >
                {importing ? '🔄 Analyse...' : '👁️ Prévisualiser'}
              </button>
              
              <button
                onClick={handleImport}
                disabled={(!file && courses.length === 0) || !selectedDepartment || !semester || !selectedAcademicYear || !selectedTrack || importing}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {importing ? '📤 Import...' : courses.length > 0 ? `📤 Importer ${courses.filter(c => c.status !== 'excluded').length} cours` : '📤 Importer'}
              </button>
            </div>
          </div>
        </div>

        {renderPreview()}
        {renderResults()}

        {/* Modal d'édition */}
        {showEditModal && editingCourse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium">✏️ Éditer le cours</h3>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={editingCourse.code}
                      onChange={(e) => setEditingCourse({...editingCourse, code: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
                    <select
                      value={editingCourse.semester}
                      onChange={(e) => setEditingCourse({...editingCourse, semester: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    >
                      <option value={1}>Semestre 1</option>
                      <option value={2}>Semestre 2</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du cours</label>
                  <input
                    type="text"
                    value={editingCourse.name}
                    onChange={(e) => setEditingCourse({...editingCourse, name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parcours</label>
                    <select
                      value={editingCourse.trackId || ''}
                      onChange={(e) => setEditingCourse({...editingCourse, trackId: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    >
                      <option value="">Sélectionner un parcours</option>
                      {tracks.map(track => (
                        <option key={track._id} value={track._id}>{track.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
                    <select
                      value={editingCourse.academicYearId || ''}
                      onChange={(e) => setEditingCourse({...editingCourse, academicYearId: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    >
                      <option value="">Sélectionner une année</option>
                      {academicYears.map(year => (
                        <option key={year._id} value={year._id}>{year.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Crédits</label>
                    <input
                      type="number"
                      value={editingCourse.credits}
                      onChange={(e) => setEditingCourse({...editingCourse, credits: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cours (h)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={editingCourse.cours}
                      onChange={(e) => setEditingCourse({...editingCourse, cours: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TD (h)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={editingCourse.td}
                      onChange={(e) => setEditingCourse({...editingCourse, td: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TP (h)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={editingCourse.tp}
                      onChange={(e) => setEditingCourse({...editingCourse, tp: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={handleSaveCourse}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                  >
                    ✓ Sauvegarder
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseImport;